import { useQueries } from "@tanstack/react-query";

import { puede, type Usuario } from "@/features/acceso/api";
import type { CustomerPage, Page, ProductReference, QuotePage } from "@/features/comercial/types";
import type { OrderRowConCierre } from "@/features/pedidos/cierre";
import { apiGet } from "@/lib/api";

import type { Opcion } from "./opciones";

/** Cuántos de cada clase: el buscador lleva a un registro, no reemplaza la lista de su pantalla. */
const LIMITE = 5;

type Fuente = { grupo: Opcion["grupo"]; ve: (u: Usuario | null | undefined) => boolean; pide: (q: string) => Promise<Opcion[]> };

const enc = encodeURIComponent;
const junto = (...partes: (string | null | undefined)[]) => partes.filter(Boolean).join(" · ") || null;
const filasDe = <T,>(data: { rows?: T[] } | null | undefined): T[] => (Array.isArray(data?.rows) ? data.rows : []);

/**
 * **Con lo que ya existe**: las búsquedas del servidor de cada lista —pedidos (`/orders/overview?q=`,
 * código, C-1-…, cliente u obra), cotizaciones (`/quotations?q=`), terceros (`/customers?q=`) y
 * referencias (`/product-references?q=`, nombre o código, también el NECA-OT-NN de la reventa y del
 * ítem manual)—, cada una solo para quien ve su pantalla. El cliente va primero: es lo que se lee.
 */
const FUENTES: Fuente[] = [
  {
    grupo: "Pedidos",
    ve: (u) => puede(u, "pedidos.ver"),
    // Ronda 25-oct B: el entregado y el quitado de la venta se encuentran igual, con su marca.
    pide: async (q) => filasDe(await apiGet<{ rows?: OrderRowConCierre[] }>(`/orders/overview?q=${enc(q)}&limit=${LIMITE}&archive=all`)).map((o) => ({
      id: `pedido:${o.id}`, grupo: "Pedidos", label: o.customer_name, detalle: junto(o.code, o.project_name, o.removed_from_sale === true ? "Quitado de la venta" : o.status === "delivered" ? "Entregado" : null), to: `/comercial/flujo/${o.id}`,
    })),
  },
  {
    grupo: "Cotizaciones",
    ve: (u) => puede(u, "cotizaciones.ver"),
    pide: async (q) => filasDe(await apiGet<QuotePage>(`/quotations?q=${enc(q)}&limit=${LIMITE}`)).map((c) => ({
      id: `cotizacion:${c.id}`, grupo: "Cotizaciones", label: c.customer_name, detalle: junto(c.code ?? "Borrador", c.project_name), to: `/comercial/cotizaciones/${c.id}`,
    })),
  },
  {
    grupo: "Clientes",
    ve: (u) => puede(u, "clientes.ver"),
    pide: async (q) => filasDe(await apiGet<CustomerPage>(`/customers?q=${enc(q)}&limit=${LIMITE}`)).map((c) => ({
      id: `cliente:${c.id}`, grupo: "Clientes", label: c.name, detalle: junto(c.code, c.nit ? `NIT ${c.nit}` : null), to: `/comercial/terceros/${c.id}`,
    })),
  },
  {
    // El catálogo vive en Costos › Referencias de producto: hace falta ver las dos cosas.
    grupo: "Referencias",
    ve: (u) => puede(u, "catalogo.ver") && puede(u, "costos.ver"),
    pide: async (q) => filasDe(await apiGet<Page<ProductReference>>(`/product-references?q=${enc(q)}&is_active=true&limit=${LIMITE}`)).map((r) => ({
      id: `referencia:${r.id}`, grupo: "Referencias", label: r.name, detalle: r.siigo_code, to: `/costos/referencias?vista=catalogo&referencia=${enc(r.name)}`,
    })),
  },
];

/**
 * Lo que se encuentra en el servidor con lo escrito (dos letras o más, ya esperado por quien llama):
 * una consulta por clase, en paralelo; una que falla no tapa a las demás.
 */
export function useBusqueda(texto: string, usuario: Usuario | null | undefined) {
  const activas = texto.length >= 2 ? FUENTES.filter((f) => f.ve(usuario)) : [];
  const consultas = useQueries({
    queries: activas.map((f) => ({ queryKey: ["buscador", f.grupo, texto], queryFn: () => f.pide(texto), staleTime: 30_000 })),
  });
  return {
    opciones: consultas.flatMap((c) => c.data ?? []),
    cargando: consultas.some((c) => c.isPending),
    fallo: consultas.some((c) => c.isError),
  };
}
